import React from 'react';
import {Navigate, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {paginaDelMenu, paginaScritta} from '../../moduli/leggi';
import {useSkin} from '../../moduli/useSkin';
import Nota from '../Vetrina/Nota';
import {TestoImpaginato} from '../Vetrina/PaginaTesto';
import {GrandiVincite, RecordDiSempre} from '../Vetrina/Vincite';
import {ITuoiPunti, LivelliFedelta} from './Fedelta';
import './Pagine.css';

/* I titoli di serie, scritti come t('…') cosi' la raccolta delle parole li vede. */
function titoloDiSerie(chiave, t) {
  if (chiave === 'fedelta') return t('Programma fedeltà');
  if (chiave === 'ruota') return t('Ruota della fortuna');
  return t('Hall of Fame');
}

/* L'intestazione di una colonna: il nome del catalogo senza l'esempio tra parentesi. */
const intestazione = (nome) => (nome || '').replace(/\s*\(.*$/, '');

/**
 * LA PARTE CHE DA' IL BACKEND, a scheletro finche' l'API non c'e' (vedi il
 * documento dell'API): il giro (ruota). Le vincite della Hall of Fame e i
 * punti della fedelta' invece ci sono (casino-service: GrandiVincite,
 * ITuoiPunti): lo scheletro resta finche' arrivano e se il servizio non
 * risponde. Niente di inventato.
 */
function Scheletro({chiave, p, userService}) {
  const {t} = useTranslation();
  if (chiave === 'ruota') {
    return (
      <div className={'pg-scheletro pg-gira'}>
        <button type={'button'} className={'pg-oro'} disabled>{t('Gira')}</button>
        <Nota tono={'spento'} piccola giocatore={t('Il giro arriva a breve.')}>
          {t('Il giro lo dà il backend (API della ruota): finché non c’è, il pulsante resta spento.')}
        </Nota>
      </div>
    );
  }
  if (chiave === 'fedelta') {
    const scheletro = (
      <div className={'pg-scheletro pg-posizione'} aria-label={t('I tuoi punti')} aria-busy={'true'}>
        <strong>{t('I tuoi punti')}</strong>
        <span className={'pg-posizione-numero'}>—</span>
        <span className={'pg-posizione-barra'}><span style={{width: 0}}/></span>
        <Nota tono={'spento'} piccola giocatore={t('I tuoi punti arrivano a breve.')}>
          {t('Punti e livello del giocatore li dà il casinò: in questo momento non risponde, qui resta lo scheletro.')}
        </Nota>
      </div>
    );
    return <ITuoiPunti scheletro={scheletro} userService={userService}/>;
  }
  const vuoto = (
    <div className={'pg-scheletro pg-classifica'} aria-label={t('Ultime grandi vincite')}>
      <div className={'pg-classifica-testa'}><strong>{t('Ultime grandi vincite')}</strong><span>{t('Dal vivo')}</span></div>
      <ul className={'pg-classifica-scheletro'} aria-busy={'true'} aria-label={t('Vincite in arrivo')}>
        {[0, 1, 2, 3].map((i) => <li key={i} className={'pg-classifica-vuota'}/>)}
      </ul>
      <Nota tono={'spento'} piccola giocatore={t('Le vincite dal vivo arrivano a breve.')}>
        {t('Le vincite vere le dà il casinò: in questo momento non risponde, qui resta lo scheletro.')}
      </Nota>
    </div>
  );
  if (!p) return vuoto;
  return <GrandiVincite periodo={p.periodo} quante={p.quante} minimo={p.minimo} scheletro={vuoto}/>;
}

/* LA RUOTA: uno spicchio per ogni premio scritto, coi colori della skin alternati all'oro. */
function Ruota({premi}) {
  const n = premi.length;
  const fetta = 360 / n;
  const colori = premi.map((_, i) => 'var(--pg-spicchio-' + (i % 3) + ') ' + (i * fetta) + 'deg ' + ((i + 1) * fetta) + 'deg').join(', ');
  return (
    <div className={'pg-ruota'} role={'img'} aria-label={premi.map((p) => p.nome).join(', ')}>
      <div className={'pg-ruota-disco'} style={{background: 'conic-gradient(' + colori + ')'}}>
        {premi.map((p, i) => (
          <span key={i} className={'pg-ruota-premio'} style={{transform: 'rotate(' + ((i + .5) * fetta) + 'deg)'}}><span>{p.nome}</span></span>
        ))}
      </div>
      <span className={'pg-ruota-perno'} aria-hidden={'true'}/>
      <span className={'pg-ruota-freccia'} aria-hidden={'true'}/>
    </div>
  );
}

/**
 * UNA PAGINA SCRITTA DEL MENU (modulo Pagine: Programma fedelta' /fedelta,
 * Ruota della fortuna /ruota, Hall of Fame /hall-of-fame): il titolo grande
 * con la prima parola in oro, una frase e un testo, i riquadri (due per
 * riga dal computer), la tabella con le colonne scritte e il pulsante in
 * fondo. La ruota disegna gli spicchi coi premi della tabella. La fedelta'
 * col backend acceso mostra i punti del giocatore e, sotto l'apertura, i
 * livelli veri del programma (Fedelta.js).
 *
 * Spenta, l'indirizzo porta alla Home. Senza niente di scritto resta il
 * posto vuoto, con scritto perche' solo in anteprima.
 */
export default function PaginaScritta({chiave, userService}) {
  useSkin();
  const {t} = useTranslation();
  const navigate = useNavigate();
  const pagina = paginaDelMenu(chiave);
  const p = paginaScritta(chiave);
  if (!pagina || !pagina.acceso) return <Navigate to={'/'} replace/>;
  const titolo = pagina.titolo === pagina.titoloDiSerie ? titoloDiSerie(chiave, t) : pagina.titolo;
  const [prima, ...resto] = (p.aperturaTitolo || titolo).split(' ');
  const vai = (dove) => {
    if (!dove) return;
    if (/^https?:\/\//i.test(dove)) window.open(dove, '_blank', 'noopener');
    else navigate(dove);
  };
  /* Con le grandi vincite vere (Hall of Fame) o i punti veri (fedelta') la pagina non e' vuota anche senza riquadri ne' tabella. */
  const vuota = !p.riquadri.length && !p.righe.length && !(p.backend && (chiave === 'hallOfFame' || chiave === 'fedelta'));
  const ruota = chiave === 'ruota' && p.righe.length > 1;
  return (
    <div className={'vetrina pg-scritta pg-pagina-' + chiave}>
      <section className={'pg-vip-apertura'} aria-label={titolo}>
        <h1><span className={'pg-vip-oro'}>{prima}</span>{resto.length > 0 && ' ' + resto.join(' ')}</h1>
        {p.aperturaRiga && <p className={'pg-vip-riga'}>{p.aperturaRiga}</p>}
        {p.aperturaTesto && <div className={'pg-vip-testo'}><TestoImpaginato testo={p.aperturaTesto}/></div>}
        {ruota && <Ruota premi={p.righe}/>}
        {p.backend && <Scheletro chiave={chiave} p={p} userService={userService}/>}
        {p.backend && p.record && chiave === 'hallOfFame' && <RecordDiSempre/>}
        {p.riquadri.length > 0 && (
          <ul className={'pg-vantaggi'}>
            {p.riquadri.map((v, i) => (
              <li key={i} className={'pg-vantaggio'}>
                <span className={'pg-vantaggio-icona'} aria-hidden={'true'}>{v.icona || '✨'}</span>
                <span className={'pg-vantaggio-testi'}><strong>{v.titolo}</strong>{v.testo && <span>{v.testo}</span>}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
      {p.backend && chiave === 'fedelta' && <LivelliFedelta/>}
      {vuota && (
        <Nota tono={'spento'} giocatore={t('Questa pagina sta arrivando.')}>
          {t('Riquadri e tabella si scrivono dal backoffice (Moduli › Pagine). Oggi non sono ancora scritti.')}
        </Nota>
      )}
      {p.righe.length > 0 && (
        <section className={'pg-corpo'} aria-label={p.tabellaTitolo || titolo}>
          {p.tabellaTitolo && <h2 className={'pg-titolo'}>{p.tabellaTitolo}</h2>}
          <div className={'pg-tabella-scorri'}>
            <table className={'pg-livelli'}>
              <thead>
                <tr>{p.colonne.map((c) => <th scope={'col'} key={c.chiave}>{intestazione(c.nome)}</th>)}</tr>
              </thead>
              <tbody>
                {p.righe.map((r, i) => (
                  <tr key={i}>
                    {p.colonne.map((c, j) => (j === 0
                      ? <th scope={'row'} key={c.chiave}><span className={'pg-livello-gemma'} aria-hidden={'true'} style={{'--i': i}}/>{r[c.chiave]}</th>
                      : <td key={c.chiave} className={'pg-col-' + c.chiave}>{r[c.chiave] || '—'}</td>))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
      {p.pulsante && p.collegamento && (
        <div className={'pg-piede'}>
          <button type={'button'} className={'pg-oro'} onClick={() => vai(p.collegamento)}>{p.pulsante}</button>
        </div>
      )}
    </div>
  );
}
